import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, FormActions, FormGrid, TextField } from "@atlas/ui/web";
import { ApiHttpError } from "@atlas/sdk";
import { atlas } from "../../../../lib/sdk";

const LISTA = "/banco/cadastros/sub-organizacoes";

export function BancoSubOrganizacoesForm() {
  const params = useParams<{ id?: string }>();
  const isNovo = params.id === "novo" || !params.id;
  const id = isNovo ? undefined : params.id;
  const nav = useNavigate();
  const qc = useQueryClient();

  const existing = useQuery({
    queryKey: ["banco", "sub-organizacao", id],
    queryFn: () => atlas.banco.getSubOrganizacao(id!),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  const [nome, setNome] = useState("");

  // Hidrata UMA vez por registro — reaplicar a cada refetch apagaria o que o
  // operador acabou de digitar (ver o mesmo cuidado no form de Tabela).
  const hidratadoDe = useRef<string | null>(null);
  useEffect(() => {
    const s = existing.data?.subOrganizacao;
    if (!s || hidratadoDe.current === s.id) return;
    hidratadoDe.current = s.id;
    setNome(s.nome);
  }, [existing.data]);

  const save = useMutation({
    mutationFn: () => atlas.banco.upsertSubOrganizacao({ id, nome: nome.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["banco", "sub-organizacoes"] });
      if (id) qc.invalidateQueries({ queryKey: ["banco", "sub-organizacao", id] });
      // A grid de operadores mostra o nome — renomear tem que aparecer la.
      qc.invalidateQueries({ queryKey: ["banco", "usuarios"] });
      nav(LISTA);
    },
  });

  const erroNome =
    save.error instanceof ApiHttpError && save.error.details
      ? (save.error.details as Record<string, string>).nome
      : undefined;

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate();
      }}
      style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 840 }}
    >
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Cadastros • Sub organizações
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>
          {id ? `Editar ${existing.data?.subOrganizacao.nome ?? ""}` : "Nova sub organização"}
        </h1>
      </header>

      <section
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <FormGrid cols={2}>
          <TextField
            label="Nome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            error={erroNome}
            hint="Aparece como opção no campo Sub organização do cadastro de operadores."
            maxLength={160}
            required
          />
        </FormGrid>
      </section>

      {save.error && !erroNome ? (
        <div style={{ color: "var(--danger-500)", fontSize: 13, padding: 12, borderRadius: 8, background: "color-mix(in srgb, var(--danger-500) 8%, transparent)", border: "1px solid var(--danger-500)" }}>
          {save.error instanceof Error ? save.error.message : "Erro ao salvar"}
        </div>
      ) : null}

      <FormActions>
        <Button variant="ghost" type="button" onClick={() => nav(LISTA)}>Cancelar</Button>
        <Button type="submit" disabled={save.isPending || !nome.trim()}>
          {save.isPending ? "Salvando..." : "Salvar"}
        </Button>
      </FormActions>
    </form>
  );
}
